import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import {
  type ReorderInspectionTemplateMutation,
  type ReorderInspectionTemplateMutationVariables,
} from 'graphql-query-contracts';
import { reorderInspectionTemplateMutation } from 'graphql-queries';

import { ReorderInspectionTemplateError } from './errors/ReorderInspectionTemplateError';

export type ReorderInspectionTemplateSectionParams = {
  sectionId: string;
  itemIds: string[];
};

export type ReorderInspectionTemplateParams = {
  channelId: string;
  templateId: string;
  sectionIds: string[];
  sections: ReorderInspectionTemplateSectionParams[];
};

export type ReorderInspectionTemplateResult = {
  success: boolean;
  newVersion: number;
};

export type UseReorderInspectionTemplateResult = {
  reorderInspectionTemplate: (
    params: ReorderInspectionTemplateParams
  ) => Promise<ReorderInspectionTemplateResult>;
  loading: boolean;
  error: Error | undefined;
};

function validateParams(params: ReorderInspectionTemplateParams): void {
  if (!params.channelId?.trim()) {
    throw new ReorderInspectionTemplateError('channelId is required');
  }

  if (!params.templateId?.trim()) {
    throw new ReorderInspectionTemplateError('templateId is required');
  }

  if (params.sectionIds.length === 0 && params.sections.length === 0) {
    throw new ReorderInspectionTemplateError(
      'At least one section order or item order is required'
    );
  }
}

export const useReorderInspectionTemplate =
  (): UseReorderInspectionTemplateResult => {
    const [reorderTemplate, { loading }] = useMutation<
      ReorderInspectionTemplateMutation,
      ReorderInspectionTemplateMutationVariables
    >(reorderInspectionTemplateMutation);

    const [error, setError] = useState<Error | undefined>(undefined);

    const reorderInspectionTemplate = useCallback(
      async (
        params: ReorderInspectionTemplateParams
      ): Promise<ReorderInspectionTemplateResult> => {
        try {
          setError(undefined);

          validateParams(params);

          const variables: ReorderInspectionTemplateMutationVariables = {
            input: {
              channelId: params.channelId,
              templateId: params.templateId,
              sectionIds: params.sectionIds,
              sections: params.sections.map(section => ({
                sectionId: section.sectionId,
                itemIds: section.itemIds,
              })),
            },
          };

          const mutationResult = await reorderTemplate({ variables });

          const payload = mutationResult.data?.reorderInspectionTemplate;

          if (!payload) {
            throw new ReorderInspectionTemplateError();
          }

          if (!payload.success) {
            throw new ReorderInspectionTemplateError(
              'Reorder inspection template did not succeed'
            );
          }

          return {
            success: payload.success,
            newVersion: payload.newVersion,
          };
        } catch (err) {
          const wrappedError =
            err instanceof ReorderInspectionTemplateError
              ? err
              : new ReorderInspectionTemplateError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to reorder inspection template'
                );

          setError(wrappedError);

          throw wrappedError;
        }
      },
      [reorderTemplate]
    );

    return {
      reorderInspectionTemplate,
      loading,
      error,
    };
  };
